<app-page-layout 
  [title]="title"
  [subtitle]="subtitle"
  [heroImage]="heroImage"
  [breadcrumbs]="breadcrumbs"
  [prevPage]="prevPage"
  [nextPage]="nextPage">
  
  <!-- Introduction -->
  <section class="page-section">
    <div class="highlight-box">
      <h2><i class="pi pi-shield"></i> Notre engagement RGPD</h2>
      
      <p><strong>Brokins</strong> s'engage à protéger la vie privée et les données personnelles de ses utilisateurs. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos données conformément au <strong>Règlement Général sur la Protection des Données (RGPD)</strong> et à la loi Informatique et Libertés.</p>
      
      <div class="update-info">
        <i class="pi pi-calendar"></i>
        <div>
          <p><strong>Dernière mise à jour :</strong> Décembre 2025</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Responsable du traitement -->
  <section class="page-section">
    <h2><i class="pi pi-user"></i> Responsable du traitement des données</h2>
    
    <div class="controller-info">
      <p><strong>BROKINS SAS</strong><br>
      SIRET : 821 159 589 00026<br>
      Code APE : 66.22Z (Activités des agents et courtiers d'assurances)<br>
      79 Rue de Rennes<br>
      75006 Paris<br>
      France</p>
    </div>
  </section>

  <!-- Données collectées -->
  <section class="page-section">
    <h2><i class="pi pi-database"></i> Données personnelles collectées</h2>
    
    <p>Nous collectons différentes catégories de données en fonction de vos interactions avec nos services :</p>
    
    <div class="data-categories">
      <div class="category-card">
        <i class="pi pi-id-card"></i>
        <h3>Données d'identification</h3>
        <ul>
          <li>Nom et prénom</li>
          <li>Date de naissance</li>
          <li>Adresse postale</li>
          <li>Adresse email</li>
          <li>Numéro de téléphone</li>
          <li>Numéro de sécurité sociale (uniquement pour les devis santé et assurance de prêt)</li>
        </ul>
      </div>
      
      <div class="category-card">
        <i class="pi pi-briefcase"></i>
        <h3>Données professionnelles</h3>
        <ul>
          <li>Profession et catégorie socio-professionnelle</li>
          <li>Revenus et situation financière</li>
          <li>Nom de l'entreprise (pour les professionnels)</li>
          <li>SIRET (pour les entreprises)</li>
        </ul>
      </div>
      
      <div class="category-card">
        <i class="pi pi-heart"></i>
        <h3>Données de santé</h3>
        <ul>
          <li>État de santé général</li>
          <li>Antécédents médicaux</li>
          <li>Traitements en cours</li>
          <li>Hospitalisations</li>
        </ul>
        <p class="sensitive-note"><i class="pi pi-exclamation-triangle"></i> Données sensibles soumises à un consentement explicite</p>
      </div>
      
      <div class="category-card">
        <i class="pi pi-desktop"></i>
        <h3>Données de navigation</h3>
        <ul>
          <li>Adresse IP</li>
          <li>Type de navigateur</li>
          <li>Pages visitées</li>
          <li>Cookies et traceurs</li>
          <li>Durée de visite</li>
        </ul>
      </div>
    </div>
  </section>

  <!-- Finalités du traitement -->
  <section class="page-section">
    <h2><i class="pi pi-list"></i> Finalités du traitement</h2>
    
    <p>Vos données personnelles sont collectées et traitées pour les finalités suivantes :</p>
    
    <div class="purposes-list">
      <div class="purpose-item">
        <div class="purpose-icon">
          <i class="pi pi-search"></i>
        </div>
        <div class="purpose-content">
          <h3>Comparaison et devis d'assurance</h3>
          <p>Établir des devis personnalisés et comparer les offres des assureurs partenaires dans les domaines santé, auto, habitation, locaux, décennale, prévoyance, crédit et multirisque professionnelle</p>
          <span class="legal-basis">Base légale : Consentement et exécution de mesures précontractuelles</span>
        </div>
      </div>
      
      <div class="purpose-item">
        <div class="purpose-icon">
          <i class="pi pi-file-edit"></i>
        </div>
        <div class="purpose-content">
          <h3>Souscription de contrats</h3>
          <p>Traiter vos demandes de souscription et gérer vos contrats d'assurance pour particuliers et professionnels en IARD et protection sociale</p>
          <span class="legal-basis">Base légale : Exécution du contrat</span>
        </div>
      </div>
      
      <div class="purpose-item">
        <div class="purpose-icon">
          <i class="pi pi-phone"></i>
        </div>
        <div class="purpose-content">
          <h3>Relation client et suivi</h3>
          <p>Répondre à vos questions, gérer vos demandes et assurer le suivi de vos contrats</p>
          <span class="legal-basis">Base légale : Intérêt légitime et exécution du contrat</span>
        </div>
      </div>
      
      <div class="purpose-item">
        <div class="purpose-icon">
          <i class="pi pi-chart-line"></i>
        </div>
        <div class="purpose-content">
          <h3>Amélioration des services</h3>
          <p>Analyser l'utilisation de nos services pour améliorer votre expérience utilisateur</p>
          <span class="legal-basis">Base légale : Intérêt légitime</span>
        </div>
      </div>
      
      <div class="purpose-item">
        <div class="purpose-icon">
          <i class="pi pi-envelope"></i>
        </div>
        <div class="purpose-content">
          <h3>Communications marketing</h3>
          <p>Vous informer de nos offres, nouveautés et conseils (uniquement avec votre consentement)</p>
          <span class="legal-basis">Base légale : Consentement</span>
        </div>
      </div>
    </div>
  </section>

  <!-- Destinataires des données -->
  <section class="page-section">
    <div class="info-box">
      <h2><i class="pi pi-users"></i> Destinataires de vos données</h2>
      
      <p>Vos données personnelles peuvent être communiquées aux destinataires suivants :</p>
      
      <ul>
        <li><strong>Assureurs partenaires</strong> : Pour l'établissement de devis et la souscription de contrats</li>
        <li><strong>Personnel autorisé de Brokins</strong> : Conseillers, service client, équipe technique</li>
        <li><strong>Prestataires techniques</strong> : Hébergement, maintenance informatique, CRM</li>
        <li><strong>Organismes de contrôle</strong> : ACPR, ORIAS, autorités judiciaires (si requis par la loi)</li>
        <li><strong>Partenaires marketing</strong> : Uniquement avec votre consentement explicite</li>
      </ul>
      
      <p class="guarantee"><i class="pi pi-lock"></i> Tous nos partenaires sont soumis à des obligations strictes de confidentialité et de sécurité.</p>
    </div>
  </section>

  <!-- Transferts hors UE -->
  <section class="page-section">
    <h2><i class="pi pi-globe"></i> Transferts de données hors UE</h2>
    
    <div class="transfer-info">
      <p>Vos données personnelles sont hébergées et traitées au sein de l'Union Européenne.</p>
      
      <p>En cas de transfert exceptionnel vers des pays tiers, nous nous assurons que :</p>
      <ul>
        <li>Le pays bénéficie d'une décision d'adéquation de la Commission Européenne</li>
        <li>Ou que des garanties appropriées sont mises en place (clauses contractuelles types)</li>
      </ul>
      
      <p>Vous serez informé de tout transfert hors UE et de vos droits dans ce contexte.</p>
    </div>
  </section>

  <!-- Durée de conservation -->
  <section class="page-section">
    <h2><i class="pi pi-clock"></i> Durée de conservation des données</h2>
    
    <div class="retention-grid">
      <div class="retention-card">
        <i class="pi pi-file"></i>
        <h3>Devis non aboutis</h3>
        <p class="duration">3 ans</p>
        <p class="description">À compter de la date du dernier contact</p>
      </div>
      
      <div class="retention-card">
        <i class="pi pi-check-circle"></i>
        <h3>Contrats en cours</h3>
        <p class="duration">Durée du contrat + 5 ans</p>
        <p class="description">Pour répondre aux obligations légales</p>
      </div>
      
      <div class="retention-card">
        <i class="pi pi-heart-fill"></i>
        <h3>Données de santé</h3>
        <p class="duration">10 ans</p>
        <p class="description">Après la fin du contrat (obligation légale)</p>
      </div>
      
      <div class="retention-card">
        <i class="pi pi-envelope"></i>
        <h3>Marketing</h3>
        <p class="duration">3 ans</p>
        <p class="description">Après le dernier contact ou retrait du consentement</p>
      </div>
    </div>
    
    <p class="note">Au-delà de ces durées, vos données sont soit supprimées, soit anonymisées.</p>
  </section>

  <!-- Vos droits -->
  <section class="page-section">
    <div class="highlight-box">
      <h2><i class="pi pi-key"></i> Vos droits sur vos données personnelles</h2>
      
      <p>Conformément au RGPD, vous disposez des droits suivants :</p>
      
      <div class="rights-grid">
        <div class="right-card">
          <i class="pi pi-eye"></i>
          <h4>Droit d'accès</h4>
          <p>Obtenir une copie de vos données personnelles</p>
        </div>
        
        <div class="right-card">
          <i class="pi pi-pencil"></i>
          <h4>Droit de rectification</h4>
          <p>Corriger vos données inexactes ou incomplètes</p>
        </div>
        
        <div class="right-card">
          <i class="pi pi-trash"></i>
          <h4>Droit à l'effacement</h4>
          <p>Demander la suppression de vos données (sous conditions)</p>
        </div>
        
        <div class="right-card">
          <i class="pi pi-ban"></i>
          <h4>Droit d'opposition</h4>
          <p>Vous opposer au traitement de vos données (marketing, profilage)</p>
        </div>
        
        <div class="right-card">
          <i class="pi pi-lock"></i>
          <h4>Droit à la limitation</h4>
          <p>Limiter temporairement le traitement de vos données</p>
        </div>
        
        <div class="right-card">
          <i class="pi pi-download"></i>
          <h4>Droit à la portabilité</h4>
          <p>Récupérer vos données dans un format structuré</p>
        </div>
        
        <div class="right-card">
          <i class="pi pi-user-minus"></i>
          <h4>Retrait du consentement</h4>
          <p>Retirer votre consentement à tout moment</p>
        </div>
        
        <div class="right-card">
          <i class="pi pi-send"></i>
          <h4>Directives post-mortem</h4>
          <p>Définir le sort de vos données après votre décès</p>
        </div>
      </div>
      
      <div class="exercise-rights">
        <h3><i class="pi pi-question-circle"></i> Comment exercer vos droits ?</h3>
        <p>Vous pouvez exercer vos droits à tout moment en nous contactant :</p>
        <ul>
          <li><strong>Email :</strong> dpo&#64;brokins.fr</li>
          <li><strong>Courrier :</strong> BROKINS SAS - Service DPO, 79 Rue de Rennes, 75006 Paris</li>
        </ul>
        <p>Nous répondrons à votre demande dans un délai maximum de <strong>1 mois</strong> à compter de sa réception.</p>
        <p class="id-note"><i class="pi pi-info-circle"></i> Une copie de votre pièce d'identité pourra vous être demandée pour vérifier votre identité.</p>
      </div>
    </div>
  </section>

  <!-- Sécurité -->
  <section class="page-section">
    <h2><i class="pi pi-shield"></i> Sécurité de vos données</h2>
    
    <p>Nous mettons en œuvre toutes les mesures techniques et organisationnelles appropriées pour protéger vos données personnelles :</p>
    
    <div class="security-measures">
      <div class="measure-item">
        <i class="pi pi-lock"></i>
        <div>
          <h4>Chiffrement SSL/TLS</h4>
          <p>Toutes les communications avec notre site sont sécurisées par protocole HTTPS</p>
        </div>
      </div>
      
      <div class="measure-item">
        <i class="pi pi-server"></i>
        <div>
          <h4>Hébergement sécurisé</h4>
          <p>Nos données sont hébergées dans des datacenters certifiés en France</p>
        </div>
      </div>
      
      <div class="measure-item">
        <i class="pi pi-user-shield"></i>
        <div>
          <h4>Accès restreints</h4>
          <p>Seules les personnes habilitées ont accès à vos données personnelles</p>
        </div>
      </div>
      
      <div class="measure-item">
        <i class="pi pi-refresh"></i>
        <div>
          <h4>Sauvegardes régulières</h4>
          <p>Vos données sont sauvegardées pour prévenir toute perte</p>
        </div>
      </div>
      
      <div class="measure-item">
        <i class="pi pi-bell"></i>
        <div>
          <h4>Détection des incidents</h4>
          <p>Systèmes de surveillance et d'alerte en cas de tentative d'intrusion</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Cookies -->
  <section class="page-section">
    <div class="info-box">
      <h2><i class="pi pi-cog"></i> Cookies et traceurs</h2>
      
      <p>Notre site utilise des cookies et autres traceurs pour améliorer votre expérience de navigation.</p>
      
      <div class="cookies-types">
        <h3>Types de cookies utilisés :</h3>
        
        <div class="cookie-type">
          <h4><i class="pi pi-check-circle"></i> Cookies essentiels (sans consentement)</h4>
          <ul>
            <li>Cookies de session et d'authentification</li>
            <li>Cookies de sécurité</li>
            <li>Cookies de préférences (langue, etc.)</li>
          </ul>
        </div>
        
        <div class="cookie-type">
          <h4><i class="pi pi-question-circle"></i> Cookies fonctionnels (consentement requis)</h4>
          <ul>
            <li>Mémorisation de vos choix et préférences</li>
            <li>Personnalisation de l'interface</li>
          </ul>
        </div>
        
        <div class="cookie-type">
          <h4><i class="pi pi-chart-bar"></i> Cookies analytiques (consentement requis)</h4>
          <ul>
            <li>Analyse du trafic et du comportement des utilisateurs</li>
            <li>Mesure d'audience et statistiques de visites</li>
          </ul>
        </div>
        
        <div class="cookie-type">
          <h4><i class="pi pi-megaphone"></i> Cookies marketing (consentement requis)</h4>
          <ul>
            <li>Publicité ciblée</li>
            <li>Mesure de l'efficacité des campagnes</li>
            <li>Partage sur les réseaux sociaux</li>
          </ul>
        </div>
      </div>
      
      <p class="cookie-control"><strong>Gestion des cookies :</strong> Vous pouvez à tout moment modifier vos préférences via le bandeau de gestion des cookies accessible en bas de page ou via les paramètres de votre navigateur.</p>
    </div>
  </section>

  <!-- Réclamation -->
  <section class="page-section">
    <h2><i class="pi pi-exclamation-circle"></i> Droit de réclamation</h2>
    
    <div class="complaint-info">
      <p>Si vous estimez que vos droits ne sont pas respectés ou que vos données personnelles ne sont pas traitées conformément à la réglementation, vous avez le droit d'introduire une réclamation auprès de la <strong>CNIL</strong> (Commission Nationale de l'Informatique et des Libertés) :</p>
      
      <div class="cnil-info">
        <h3><i class="pi pi-building"></i> CNIL</h3>
        <p>
          3 Place de Fontenoy<br>
          TSA 80715<br>
          75334 Paris Cedex 07<br>
          <strong>Téléphone :</strong> 01 53 73 22 22<br>
          <strong>Site web :</strong> <a href="https://www.cnil.fr" target="_blank" rel="noopener">www.cnil.fr</a>
        </p>
      </div>
    </div>
  </section>

  <!-- Modifications -->
  <section class="page-section">
    <div class="update-policy">
      <h2><i class="pi pi-refresh"></i> Modifications de la politique</h2>
      
      <p>Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment pour refléter les évolutions légales, réglementaires ou de nos pratiques.</p>
      
      <p>En cas de modification substantielle, nous vous en informerons par email ou via une notification sur notre site.</p>
      
      <p><strong>Nous vous invitons à consulter régulièrement cette page pour rester informé de nos pratiques en matière de protection des données.</strong></p>
    </div>
  </section>

  <!-- CTA -->
  <section class="page-section">
    <div class="cta-box">
      <h3>Des questions sur vos données ?</h3>
      <p>Notre DPO est à votre écoute pour répondre à toutes vos questions</p>
      <button class="btn-cta" (click)="navigateToContact()">
        <i class="pi pi-envelope"></i> Contacter le DPO
      </button>
    </div>
  </section>

</app-page-layout>